import { useState } from 'react';
import { crewsAPI } from '../services/api';
import { useNotifications } from '../components/notifications';
import { canAssignRowerToSeat } from '../utils/riggingValidation';

export const useDragAndDrop = (selectedCrew, setSelectedCrew, crews, setCrews) => {
  const [draggedRower, setDraggedRower] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [isProcessingDrop, setIsProcessingDrop] = useState(false);
  const { showToast } = useNotifications();
  const handleDragStart = (e, rower) => {
    if (!rower) {
      return;
    }

    // Set both state and dataTransfer for redundancy
    setDraggedRower(rower);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(rower));
    e.dataTransfer.setData('text/plain', rower.id.toString()); // Fallback
  };

  const handleDragEnter = (e, seat_number) => {
    e.preventDefault();
    setDropTarget(seat_number);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e) => {
    // Only clear drop target if we're actually leaving the drop zone
    // Check if the related target is outside the current target
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget(null);
    }
  };

  const handleDrop = async (e, seat_number) => {
    e.preventDefault();
    setDropTarget(null);

    // Prevent duplicate requests if already processing a drop
    if (isProcessingDrop) {
      console.log('Drop already in progress, ignoring duplicate request');
      return;
    }

    setIsProcessingDrop(true);

    // Get rower data from multiple sources for redundancy
    let rowerData = draggedRower;

    // If state is null, try to get from dataTransfer
    if (!rowerData) {
      try {
        const jsonData = e.dataTransfer.getData('application/json');
        if (jsonData) {
          rowerData = JSON.parse(jsonData);
        }
      } catch (error) {
        // Silent fallback
      }
    }

    if (!rowerData || !selectedCrew) {
      showToast('Failed to get rower data for assignment', 'error');
      setIsProcessingDrop(false);
      return;
    }

    // Validate side preference if dropping to a specific seat
    if (typeof seat_number === 'number') {
      console.log('Side preference validation debug:', {
        rowerData,
        rowerPreferredSide: rowerData.preferred_side || rowerData.side_preference,
        selectedCrew,
        seat_number,
        crewSidePreferenceStrict: selectedCrew.side_preference_strict,
        crewRiggingConfig: selectedCrew.rigging_config,
      });

      const validation = canAssignRowerToSeat(selectedCrew, rowerData, seat_number, {
        skipExperimentalCrews: true,
      });

      console.log('Validation result:', validation);

      if (!validation.canAssign) {
        if (validation.message.includes('Rigging configuration is required')) {
          showToast(`Assignment blocked: ${validation.message}`, 'error');
          setIsProcessingDrop(false);
          return;
        } else {
          showToast(`Side preference warning: ${validation.message}`, 'warning');
          // Continue with assignment but show warning
        }
      }
    }

    try {
      console.log('Adding member to crew:', { rowerData, selectedCrew, seat_number });

      // Prepare member data for API call
      const memberData = {
        user_id: rowerData.id,
      };

      // Add seat_number if it's a valid seat (not general crew drop)
      if (typeof seat_number === 'number') {
        memberData.seat_number = seat_number;
      }

      // Call API to add member to crew
      await crewsAPI.addCrewMember(selectedCrew.id, memberData);

      // Refresh crew data
      const updatedCrew = await crewsAPI.getCrew(selectedCrew.id);

      setSelectedCrew(updatedCrew);
      setCrews(crews.map((crew) => (crew.id === selectedCrew.id ? updatedCrew : crew)));
      showToast('Rower assigned successfully', 'success');
    } catch (error) {
      console.error('Drop error:', error);
      showToast('Failed to assign rower', 'error');
    } finally {
      setDraggedRower(null);
      setIsProcessingDrop(false);
    }
  };

  const handleDragEnd = (_e) => {
    // Clean up drag state
    setDraggedRower(null);
    setDropTarget(null);
    setIsProcessingDrop(false);
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedCrew) return;

    try {
      await crewsAPI.removeCrewMember(selectedCrew.id, memberId);

      // Refresh crew data
      const updatedCrew = await crewsAPI.getCrew(selectedCrew.id);

      setSelectedCrew(updatedCrew);
      setCrews(crews.map((crew) => (crew.id === selectedCrew.id ? updatedCrew : crew)));
      showToast('Rower removed successfully', 'success');
    } catch (error) {
      showToast('Failed to remove rower', 'error');
    }
  };

  return {
    draggedRower,
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleRemoveMember,
  };
};
